// ENG-2648: show the customer the reference the backend already generated.
//
// `failRequest` logs `<what was attempted> tenant=… ref=… cause=…` and answers `{error, code?, ref?}`.
// The ref is Cloud Run's own `X-Cloud-Trace-Context` (or an inbound `X-Request-Id`), so quoting it
// turns "the console errored" into a single log query.
//
// Every client read `data.error` and dropped `data.ref` on the floor. That left the feature half
// built in the worst way: support could find the log **if** the customer quoted a reference the
// product never showed them. Surfacing it is what makes the backend half worth anything.
//
// Appended to the message rather than given its own field, deliberately. Every error surface in the
// console already renders a string — alerts, inline copy, snackbars, the sync report — so this
// reaches all of them at once instead of requiring each to learn a new shape. A second state
// threaded through a dozen call sites would be a bigger change with the same result.

/**
 * Append the backend's error reference to a message, when there is one.
 *
 * Absence is the common case and must stay silent: `failRequest` omits `ref` when the request
 * carried no trace header, and rendering "Reference: undefined" is worse than rendering nothing.
 *
 * [ref] is typed `unknown` because it comes from `await response.json()`, which is `any` — the guard
 * here is the only thing standing between a malformed body and that string.
 */
export const withRef = (message: string, ref: unknown): string =>
  typeof ref === "string" && ref.trim() !== "" ? `${message} (reference: ${ref})` : message;

/**
 * The message for a failed response body, with the reference attached.
 *
 * [fallback] is used when the body has no `error` — a 502 from the load balancer, or a body that
 * isn't JSON at all. The ref is still appended if one somehow survived, since a reference without a
 * message is still worth quoting.
 *
 * [fallback] may be a thunk. Pass one whenever building it does work worth skipping on the common
 * path — `fallbackError` logs to `console.warn`, and a plain string argument evaluates on every
 * call regardless of which branch below actually uses it. ENG-3470 caught this the hard way: a 409
 * "Request already decided." (a real `data.error`) still logged "Couldn't deny the request. (409)"
 * because the fallback was built eagerly as an argument before this function ever looked at `data`.
 */
export const errorWithRef = (body: unknown, fallback: string | (() => string)): string => {
  const data = (body ?? {}) as { error?: unknown; ref?: unknown };
  const message = typeof data.error === "string" && data.error.trim() !== ""
    ? data.error
    : typeof fallback === "function" ? fallback() : fallback;
  return withRef(message, data.ref);
};

/**
 * The fallback text for a failed request whose body carried no `error` — pass this to
 * [errorWithRef]'s [fallback] rather than building `"${subject} (${status})."` by hand.
 *
 * ENG-3470: a customer reading "Could not load your organization's members (503)." learns nothing —
 * a status code is not a sentence, and it reads like the product broke rather than like a retry
 * would help. The code is still worth having: it goes to `console.warn` so it is one browser-console
 * glance away, same place someone would already look for a stack trace. What replaces it in the
 * sentence is "Try again in a moment.", the one thing every one of these fallbacks is a candidate
 * for, since a caller only reaches this branch when the backend declined to say anything more
 * specific — the concrete, backend-authored reasons ("All seats are filled…") come from `data.error`
 * and never pass through here.
 */
export const fallbackError = (subject: string, response: Response): string => {
  console.warn(`${subject} (${response.status})`);
  return `${subject} Try again in a moment.`;
};
